import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getSession } from "../functions";
import axios from "axios";

// ✅ Applies dynamic discount % to Cash payment mode based on dcrEntries
const applyDiscountIfNeeded = (receiptDate, feeDetails, dcrEntries = []) => {
  const formatted = new Date(receiptDate).toLocaleDateString("en-GB");

  const matchedDcr = dcrEntries.find(
    (d) => new Date(d.date).toLocaleDateString("en-GB") === formatted,
  );

  if (!matchedDcr || !matchedDcr.discount || Number(matchedDcr.discount) === 0)
    return feeDetails;

  const discountPercent = Number(matchedDcr.discount) / 100;

  const applyToPaymentMode = (paymentMode = {}) => {
    const updated = { ...paymentMode };
    if (updated["Cash"]) {
      updated["Cash"] = {
        ...updated["Cash"],
        amount: updated["Cash"].amount * (1 - discountPercent),
      };
    }
    return updated;
  };

  const applyToFeeHead = (feeHead) => {
    if (!feeHead?.paid) return feeHead;
    const updatedPaymentMode = applyToPaymentMode(feeHead.paymentMode);
    const cashDiscount =
      (feeHead.paymentMode?.Cash?.amount || 0) * discountPercent;
    return {
      ...feeHead,
      TotalAmt: feeHead.TotalAmt - cashDiscount,
      paymentMode: updatedPaymentMode,
    };
  };

  const updatedMonthPaid = (feeDetails.monthPaid || []).map((m) => {
    if (!m.paid) return m;
    const updatedPaymentMode = applyToPaymentMode(m.paymentMode);
    const cashDiscount = (m.paymentMode?.Cash?.amount || 0) * discountPercent;
    const newMonthlyFee =
      m.monthlyFee -
      (m.paymentMode?.Cash
        ? cashDiscount * (m.monthlyFee / (m.TotalAmt || 1))
        : 0);
    const newTransportFee =
      m.transportFee -
      (m.paymentMode?.Cash
        ? cashDiscount * (m.transportFee / (m.TotalAmt || 1))
        : 0);
    return {
      ...m,
      TotalAmt: (m.TotalAmt || 0) - cashDiscount,
      monthlyFee: newMonthlyFee,
      transportFee: newTransportFee,
      paymentMode: updatedPaymentMode,
    };
  });

  return {
    ...feeDetails,
    admissionFee: applyToFeeHead(feeDetails.admissionFee),
    developmentCharges: applyToFeeHead(feeDetails.developmentCharges),
    monthPaid: updatedMonthPaid,
  };
};

const DcrSchoolPDF = async (
  feeData,
  discountMode = "original",
  admissionFormData = [],
  miscellaneousFeesData = [],
  selectedModes = [],
) => {
  if (!feeData || !Array.isArray(feeData) || feeData.length === 0) {
    alert("No data available to export.");
    return;
  }

  // ✅ Mode filter:
  // - ["All"] / empty  => original "All in One" behaviour, everything included.
  // - Payment mode names (Cash/UPI/NEFT/...) => only those mode-wise
  //   collection tables (+ their Total Collection Summary) are included.
  // - "Admission Form Fee" / "Misc Fee" => independent on/off toggles for
  //   those two report sections. If not picked (and "All" isn't picked),
  //   the section is left out of the PDF entirely.
  const SECTION_KEYS = ["Admission Form Fee", "Misc Fee"];
  const allModesSelected =
    !selectedModes ||
    selectedModes.length === 0 ||
    selectedModes.includes("All");

  const selectedPaymentModes = allModesSelected
    ? []
    : selectedModes.filter((m) => !SECTION_KEYS.includes(m) && m !== "All");

  let hasPaymentModeSelection =
    allModesSelected || selectedPaymentModes.length > 0;
  const isModeAllowed = (mode) =>
    allModesSelected || selectedPaymentModes.includes(mode);

  const includeAdmissionForm =
    allModesSelected || selectedModes.includes("Admission Form Fee");
  const includeMiscFee = allModesSelected || selectedModes.includes("Misc Fee");

  // ✅ Fetch dcrEntries from API
  let dcrEntries = [];
  try {
    const response = await axios.get(
      "https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/GetGeneral",
      {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      },
    );
    dcrEntries = response.data?.data?.[0]?.dcr || [];
  } catch (err) {
    console.error("Failed to fetch DCR entries:", err);
  }

  const reportDate = feeData[0]?.toDate
    ? `${new Date(feeData[0].date).toLocaleDateString("en-GB")} to ${new Date(
        feeData[0].toDate,
      ).toLocaleDateString("en-GB")}`
    : new Date(feeData[0]?.date).toLocaleDateString("en-GB");
  const session = getSession();

  let allFeesData = feeData.flatMap((entry) => entry.monthlyFees || []);

  let modeWiseData = {};
  let modeWiseTotals = {};
  let modeHeadWiseTotals = {};
  let multiModeData = [];
  let totalAdmissionFee = 0;
  let totalDevelopmentCharges = 0;
  let totalMonthlyFee = 0;
  let totalTransportFee = 0;

  allFeesData.forEach((student) => {
    const {
      admission_no,
      receipt_no,
      date,
      student_name,
      class: studentClass,
      currentSection,
      collectedBy,
      feeDetails: rawFeeDetails,
    } = student;

    if (!rawFeeDetails || !rawFeeDetails.monthPaid) return;

    // ✅ Apply discount only when discountMode is "discounted"
    const feeDetails =
      discountMode === "discounted"
        ? applyDiscountIfNeeded(date, rawFeeDetails, dcrEntries)
        : rawFeeDetails;

    let studentTotalAmount = 0;
    let paymentModes = {};
    let detailsList = new Set();

    const addPayment = (fee, feeType) => {
      if (fee?.paid) {
        studentTotalAmount += fee.TotalAmt || 0;
        if (feeType === "admission") totalAdmissionFee += fee.TotalAmt || 0;
        if (feeType === "development")
          totalDevelopmentCharges += fee.TotalAmt || 0;

        Object.entries(fee.paymentMode || {}).forEach(
          ([mode, { amount, detail, bank }]) => {
            paymentModes[mode] = (paymentModes[mode] || 0) + amount;
            if (!modeHeadWiseTotals[mode]) {
              modeHeadWiseTotals[mode] = {
                admission: 0,
                development: 0,
                monthly: 0,
                transport: 0,
              };
            }
            modeHeadWiseTotals[mode][feeType] += amount;
            if (detail) detailsList.add(`Detail: ${detail}`);
            if (bank) detailsList.add(`Bank: ${bank}`);
          },
        );
      }
    };

    addPayment(feeDetails.developmentCharges, "development");
    addPayment(feeDetails.admissionFee, "admission");

    let monthlyFeeTotal = 0;
    let transportFeeTotal = 0;
    let monthPaidList = feeDetails.monthPaid
      .filter((m) => m.paid)
      .map((m) => {
        monthlyFeeTotal += m.monthlyFee ?? 0;
        transportFeeTotal += m.transportFee ?? 0;
        totalMonthlyFee += m.monthlyFee ?? 0;
        totalTransportFee += m.transportFee ?? 0;
        studentTotalAmount += m.TotalAmt ?? 0;

        Object.entries(m.paymentMode || {}).forEach(
          ([mode, { amount, detail, bank }]) => {
            paymentModes[mode] = (paymentModes[mode] || 0) + amount;
            if (!modeHeadWiseTotals[mode]) {
              modeHeadWiseTotals[mode] = {
                admission: 0,
                development: 0,
                monthly: 0,
                transport: 0,
              };
            }
            modeHeadWiseTotals[mode].monthly += m.monthlyFee ?? 0;
            modeHeadWiseTotals[mode].transport += m.transportFee ?? 0;
            if (detail) detailsList.add(`Detail: ${detail}`);
            if (bank) detailsList.add(`Bank: ${bank}`);
          },
        );
        return m.month;
      })
      .join(", ");

    if (Object.keys(paymentModes).length > 1) {
      multiModeData.push({
        receipt_no,
        admission_no,
        date: new Date(date).toLocaleDateString(),
        student_name,
        classSection: `${studentClass}, ${currentSection}`,
        developmentCharges: feeDetails.developmentCharges?.TotalAmt || 0,
        admissionFee: feeDetails.admissionFee?.TotalAmt || 0,
        monthsPaid: monthPaidList,
        monthlyFee: monthlyFeeTotal,
        transportFee: transportFeeTotal,
        modeAmounts: paymentModes,
        totalAmount: studentTotalAmount,
        collectedBy: collectedBy || "N/A",
        txnDetails: [...detailsList].join("\n") || "N/A",
      });
    } else {
      Object.entries(paymentModes).forEach(([mode, amount]) => {
        if (!modeWiseData[mode]) modeWiseData[mode] = [];
        modeWiseData[mode].push([
          modeWiseData[mode].length + 1,
          receipt_no,
          admission_no,
          new Date(date).toLocaleDateString(),
          student_name,
          `${studentClass}, ${currentSection}`,
          (feeDetails.developmentCharges?.TotalAmt || 0).toLocaleString(),
          (feeDetails.admissionFee?.TotalAmt || 0).toLocaleString(),
          monthPaidList,
          monthlyFeeTotal.toLocaleString(),
          transportFeeTotal.toLocaleString(),
          studentTotalAmount.toLocaleString(),
          collectedBy || "N/A",
          [...detailsList].join("\n") || "N/A",
        ]);

        if (!modeWiseTotals[mode]) {
          modeWiseTotals[mode] = {
            development: 0,
            admission: 0,
            monthly: 0,
            transport: 0,
            paidAmount: 0,
          };
        }
        modeWiseTotals[mode].development +=
          feeDetails.developmentCharges?.TotalAmt || 0;
        modeWiseTotals[mode].admission +=
          feeDetails.admissionFee?.TotalAmt || 0;
        modeWiseTotals[mode].monthly += monthlyFeeTotal;
        modeWiseTotals[mode].transport += transportFeeTotal;
        modeWiseTotals[mode].paidAmount += amount;
      });
    }
  });

  // ✅ Restrict the payment-mode-keyed structures down to the selected
  // mode(s). If specific payment modes were chosen but none of them had
  // any matching records, disable the payment-mode section for this run
  // (rather than aborting the whole report — the Admission Form Fee /
  // Misc Fee sections may still have data to show).
  if (!allModesSelected) {
    if (!hasPaymentModeSelection) {
      modeWiseData = {};
      modeWiseTotals = {};
      modeHeadWiseTotals = {};
    } else {
      Object.keys(modeWiseData).forEach((mode) => {
        if (!isModeAllowed(mode)) {
          delete modeWiseData[mode];
          delete modeWiseTotals[mode];
          delete modeHeadWiseTotals[mode];
        }
      });

      if (Object.keys(modeWiseData).length === 0) {
        hasPaymentModeSelection = false;
      }
    }
  }

  // ✅ Nothing at all was requested / matched — bail out with a clear message.
  if (!hasPaymentModeSelection && !includeAdmissionForm && !includeMiscFee) {
    alert("No records found for the selected report mode(s).");
    return;
  }

  // ── Build PDF ─────────────────────────────────────────────────────────────
  const doc = new jsPDF({ orientation: "l", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  const schoolLogo = "/assets/images/icon.png";
  const imageWidth = 20;
  const imageHeight = 20;
  const imageX = pageWidth / 2 - imageWidth / 2;

  doc.addImage(schoolLogo, "PNG", imageX, 10, imageWidth, imageHeight);

  doc.setFont("helvetica", "bold").setFontSize(16);
  doc.text("Acharyakulam", pageWidth / 2, 35, { align: "center" });
  doc.setFont("helvetica", "normal").setFontSize(12);
  doc.text(
    "Near Namkum Police Station, Tata Road, Namkum, Ranchi - 834010",
    pageWidth / 2,
    40,
    { align: "center" },
  );
  doc.setFont("helvetica", "bold").setFontSize(13);
  doc.text(`Session ${session}`, pageWidth / 2, 47, { align: "center" });
  doc.text(`School Fee`, pageWidth / 2, 52, { align: "center" });
  doc.text(`Daily Collection Report (DCR)`, pageWidth / 2, 58, {
    align: "center",
  });
  doc.text(`${reportDate}`, pageWidth / 2, 64, { align: "center" });

  // ✅ Show discount indicator and/or selected-mode indicator in header
  let indicatorY = 69;

  if (discountMode === "discounted") {
    doc.setFontSize(10);
    doc.setTextColor(239, 78, 36);
    doc.text(
      `(Discounted Report — Cash Discount Applied)`,
      pageWidth / 2,
      indicatorY,
      { align: "center" },
    );
    doc.setTextColor(0, 0, 0);
    indicatorY += 5;
  }

  if (!allModesSelected) {
    doc.setFontSize(10);
    doc.setTextColor(70, 70, 70);
    doc.text(
      `(Mode Report — ${selectedModes.join(", ")})`,
      pageWidth / 2,
      indicatorY,
      { align: "center" },
    );
    doc.setTextColor(0, 0, 0);
    indicatorY += 5;
  }

  let currentY = indicatorY + (indicatorY > 69 ? 4 : 6);

  // ── MODE WISE TABLES ──────────────────────────────────────────────────────
  Object.entries(modeWiseData).forEach(([mode, data]) => {
    doc.text(`[${mode} Collection]`, pageWidth / 2, currentY, {
      align: "center",
    });
    autoTable(doc, {
      startY: currentY + 5,
      head: [
        [
          "SL No.",
          "Receipt No.",
          "Admission No.",
          "Date",
          "Student Name",
          "Class & Section",
          "Development Charges",
          "Admission Fee",
          "Months Paid",
          "Monthly Fee",
          "Transport Fee",
          "Total Amount",
          "Collected By",
          "Txn. Details",
        ],
      ],
      body: [
        ...data,
        [
          "Total",
          "",
          "",
          "",
          "",
          "",
          modeWiseTotals[mode].development.toLocaleString(),
          modeWiseTotals[mode].admission.toLocaleString(),
          "",
          modeWiseTotals[mode].monthly.toLocaleString(),
          modeWiseTotals[mode].transport.toLocaleString(),
          modeWiseTotals[mode].paidAmount.toLocaleString(),
          "",
          "",
        ],
      ],
      theme: "grid",
      styles: { fontSize: 6, halign: "center", cellPadding: 2 },
      headStyles: { fillColor: [239, 78, 36], textColor: [255, 255, 255] },
      didParseCell: function (data) {
        if (data.row.index === data.table.body.length - 1) {
          data.cell.styles.fillColor = [220, 230, 241];
          data.cell.styles.fontStyle = "bold";
        }
      },
    });
    currentY = doc.lastAutoTable.finalY + 10;
  });

  /* ── MULTI PAYMENT MODE COLLECTION TABLE — COMMENTED OUT FOR NOW ──────────

  doc.addPage();

  let multiTotals = {
    admission: 0,
    development: 0,
    monthly: 0,
    transport: 0,
    modeWise: {},
  };

  multiModeData.forEach((row) => {
    multiTotals.admission += row.admissionFee || 0;
    multiTotals.development += row.developmentCharges || 0;
    multiTotals.monthly += row.monthlyFee || 0;
    multiTotals.transport += row.transportFee || 0;

    Object.entries(row.modeAmounts || {}).forEach(([mode, amt]) => {
      multiTotals.modeWise[mode] = (multiTotals.modeWise[mode] || 0) + amt;
    });
  });

  doc.setFont("helvetica", "bold").setFontSize(13);
  doc.text(`[Multi Mode Collection]`, pageWidth / 2, 20, { align: "center" });
  autoTable(doc, {
    startY: 25,
    head: [
      [
        "SL No.",
        "Receipt No.",
        "Admission No.",
        "Date",
        "Student Name",
        "Class & Section",
        "Development Charges",
        "Admission Fee",
        "Months Paid",
        "Monthly Fee",
        "Transport Fee",
        "Amount Mode Wise",
        "Total Amount",
        "Collected By",
        "Txn. Details",
      ],
    ],
    body: multiModeData.length
      ? multiModeData.map((row, i) => {
          const amountModeWise = Object.entries(row.modeAmounts || {})
            .map(([mode, amt]) => `${mode}: ${(amt || 0).toLocaleString()}`)
            .join(", ");
          return [
            i + 1,
            row.receipt_no || "",
            row.admission_no || "",
            row.date || "",
            row.student_name || "",
            row.classSection || "",
            (row.developmentCharges || 0).toLocaleString(),
            (row.admissionFee || 0).toLocaleString(),
            row.monthsPaid || "-",
            (row.monthlyFee || 0).toLocaleString(),
            (row.transportFee || 0).toLocaleString(),
            amountModeWise || "-",
            (row.totalAmount || 0).toLocaleString(),
            row.collectedBy || "N/A",
            row.txnDetails || "-",
          ];
        })
      : [
          [
            "No Multi Payment Receipts",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
          ],
        ],
    theme: "grid",
    styles: { fontSize: 6, halign: "center", cellPadding: 2 },
    headStyles: { fillColor: [239, 78, 36], textColor: [255, 255, 255] },
  });

  const summaryBody = [
    ["Total Admission Fee", multiTotals.admission.toLocaleString()],
    ["Total Development Charges", multiTotals.development.toLocaleString()],
    ["Total Monthly Fee", multiTotals.monthly.toLocaleString()],
    ["Total Transport Fee", multiTotals.transport.toLocaleString()],
    ...Object.entries(multiTotals.modeWise).map(([mode, amt]) => [
      `Total ${mode} Amount`,
      amt.toLocaleString(),
    ]),
    [
      "Grand Total",
      Object.values(multiTotals.modeWise)
        .reduce((a, b) => a + b, 0)
        .toLocaleString(),
    ],
  ];

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 5,
    head: [["Multi Mode Collection Summary", "Amount"]],
    body: summaryBody,
    theme: "grid",
    styles: { fontSize: 7, halign: "center", cellPadding: 2 },
    headStyles: { fillColor: [239, 78, 36], textColor: [255, 255, 255] },
  });

  ── END MULTI PAYMENT MODE COLLECTION TABLE ────────────────────────────── */

  // ── TOTAL SUMMARY TABLE (only when at least one payment mode included) ───
  let modeHeadWiseData = [];

  if (hasPaymentModeSelection) {
    doc.addPage();

    doc.setFont("helvetica", "bold").setFontSize(13);
    doc.text(`[Total Collection Summary]`, pageWidth / 2, 20, {
      align: "center",
    });

    modeHeadWiseData = Object.entries(modeHeadWiseTotals).map(
      ([mode, totals]) => {
        return [
          mode,
          (modeWiseTotals[mode]?.admission || 0).toLocaleString(),
          (modeWiseTotals[mode]?.development || 0).toLocaleString(),
          (modeWiseTotals[mode]?.monthly || 0).toLocaleString(),
          (modeWiseTotals[mode]?.transport || 0).toLocaleString(),
          (modeWiseTotals[mode]?.paidAmount || 0).toLocaleString(),
        ];
      },
    );

    const totalSingleAdmission = Object.values(modeWiseTotals).reduce(
      (sum, t) => sum + (t.admission || 0),
      0,
    );
    const totalSingleDevelopment = Object.values(modeWiseTotals).reduce(
      (sum, t) => sum + (t.development || 0),
      0,
    );
    const totalSingleMonthly = Object.values(modeWiseTotals).reduce(
      (sum, t) => sum + (t.monthly || 0),
      0,
    );
    const totalSingleTransport = Object.values(modeWiseTotals).reduce(
      (sum, t) => sum + (t.transport || 0),
      0,
    );
    const totalSinglePaid = Object.values(modeWiseTotals).reduce(
      (sum, t) => sum + (t.paidAmount || 0),
      0,
    );

    modeHeadWiseData.push([
      "Grand Total",
      totalSingleAdmission.toLocaleString(),
      totalSingleDevelopment.toLocaleString(),
      totalSingleMonthly.toLocaleString(),
      totalSingleTransport.toLocaleString(),
      totalSinglePaid.toLocaleString(),
    ]);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "Payment Mode",
          "Admission Fee",
          "Development Charges",
          "Monthly Fee",
          "Transport Fee",
          "Total Amount",
        ],
      ],
      body: modeHeadWiseData,
      theme: "grid",
      styles: { fontSize: 6, halign: "center", cellPadding: 2 },
      headStyles: { fillColor: [239, 78, 36], textColor: [255, 255, 255] },
      didParseCell: function (data) {
        if (data.row.index === modeHeadWiseData.length - 1) {
          data.cell.styles.fillColor = [220, 230, 241];
          data.cell.styles.fontStyle = "bold";
        }
      },
    });
  }

  // ── ADMISSION FORM FEE TABLE (only when "Admission Form Fee" is picked,
  //    or "All in One") ────────────────────────────────────────────────────
  let admissionFormModeWiseTotals = {};
  let admissionFormGrandTotal = 0;

  if (includeAdmissionForm) {
    doc.addPage();

    const admissionFormTableBody = (admissionFormData || []).map((item, i) => {
      const amount = Number(item.amount) || 0;
      admissionFormGrandTotal += amount;

      const mode = item.mode || "N/A";
      admissionFormModeWiseTotals[mode] =
        (admissionFormModeWiseTotals[mode] || 0) + amount;

      return [
        i + 1,
        item.formNo || "",
        item.applyDate || "",
        item?.student?.name || "",
        item?.student?.Class || "",
        item?.student?.fatherName || "",
        item?.student?.contactNo || "",
        amount.toLocaleString(),
        mode,
        item.paymentId || "-",
        item.bank || "-",
        item.collectedBy || "N/A",
      ];
    });

    doc.setFont("helvetica", "bold").setFontSize(13);
    doc.text(`[Admission Form Fee Collection]`, pageWidth / 2, 20, {
      align: "center",
    });

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "SL No.",
          "Form No.",
          "Apply Date",
          "Student Name",
          "Class",
          "Father's Name",
          "Contact No.",
          "Amount",
          "Mode",
          "Payment ID",
          "Bank",
          "Collected By",
        ],
      ],
      body: admissionFormTableBody.length
        ? [
            ...admissionFormTableBody,
            [
              "Total",
              "",
              "",
              "",
              "",
              "",
              "",
              admissionFormGrandTotal.toLocaleString(),
              "",
              "",
              "",
              "",
            ],
          ]
        : [
            [
              "No Admission Form Fee Records",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
            ],
          ],
      theme: "grid",
      styles: { fontSize: 6, halign: "center", cellPadding: 2 },
      headStyles: { fillColor: [239, 78, 36], textColor: [255, 255, 255] },
      didParseCell: function (data) {
        if (
          admissionFormTableBody.length &&
          data.row.index === admissionFormTableBody.length
        ) {
          data.cell.styles.fillColor = [220, 230, 241];
          data.cell.styles.fontStyle = "bold";
        }
      },
    });
  }

  // ── MISCELLANEOUS FEE TABLE (only when "Misc Fee" is picked, or
  //    "All in One") ────────────────────────────────────────────────────────
  let miscModeWiseTotals = {};
  let miscGrandTotal = 0;

  if (includeMiscFee) {
    doc.addPage();

    const miscTableBody = (miscellaneousFeesData || []).map((item, i) => {
      const amount = Number(item.amount) || 0;
      miscGrandTotal += amount;

      const mode = item.mode || "N/A";
      miscModeWiseTotals[mode] = (miscModeWiseTotals[mode] || 0) + amount;

      return [
        i + 1,
        item.receipt_no || "",
        item.formNo || "",
        item.studentName || "",
        item.type || "",
        amount.toLocaleString(),
        mode,
        item.paymentId || "-",
        item.bank || "-",
        item.collectedBy || "N/A",
      ];
    });

    doc.setFont("helvetica", "bold").setFontSize(13);
    doc.text(`[Miscellaneous Fee Collection]`, pageWidth / 2, 20, {
      align: "center",
    });

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "SL No.",
          "Receipt No.",
          "Form No.",
          "Student Name",
          "Type",
          "Amount",
          "Mode",
          "Payment ID",
          "Bank",
          "Collected By",
        ],
      ],
      body: miscTableBody.length
        ? [
            ...miscTableBody,
            [
              "Total",
              "",
              "",
              "",
              "",
              miscGrandTotal.toLocaleString(),
              "",
              "",
              "",
              "",
            ],
          ]
        : [
            [
              "No Miscellaneous Fee Records",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
            ],
          ],
      theme: "grid",
      styles: { fontSize: 6, halign: "center", cellPadding: 2 },
      headStyles: { fillColor: [239, 78, 36], textColor: [255, 255, 255] },
      didParseCell: function (data) {
        if (miscTableBody.length && data.row.index === miscTableBody.length) {
          data.cell.styles.fillColor = [220, 230, 241];
          data.cell.styles.fontStyle = "bold";
        }
      },
    });
  }

  // ── FINAL PAGE: COMPLETE SUMMARY (only if either section above ran) ──────
  if (includeAdmissionForm || includeMiscFee) {
    doc.addPage();

    doc.setFont("helvetica", "bold").setFontSize(13);
    doc.text(
      `[Summary — Admission Form & Miscellaneous Fee]`,
      pageWidth / 2,
      20,
      { align: "center" },
    );

    const allModes = Array.from(
      new Set([
        ...Object.keys(admissionFormModeWiseTotals),
        ...Object.keys(miscModeWiseTotals),
      ]),
    );

    const completeSummaryBody = allModes.map((mode) => {
      const admFee = admissionFormModeWiseTotals[mode] || 0;
      const miscFee = miscModeWiseTotals[mode] || 0;
      return [
        mode,
        admFee.toLocaleString(),
        miscFee.toLocaleString(),
        (admFee + miscFee).toLocaleString(),
      ];
    });

    completeSummaryBody.push([
      "Grand Total",
      admissionFormGrandTotal.toLocaleString(),
      miscGrandTotal.toLocaleString(),
      (admissionFormGrandTotal + miscGrandTotal).toLocaleString(),
    ]);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "Payment Mode",
          "Admission Form Fee",
          "Miscellaneous Fee",
          "Total Amount",
        ],
      ],
      body: completeSummaryBody,
      theme: "grid",
      styles: { fontSize: 8, halign: "center", cellPadding: 3 },
      headStyles: { fillColor: [239, 78, 36], textColor: [255, 255, 255] },
      didParseCell: function (data) {
        if (data.row.index === completeSummaryBody.length - 1) {
          data.cell.styles.fillColor = [220, 230, 241];
          data.cell.styles.fontStyle = "bold";
        }
      },
    });
  }

  const fileName = "Report.pdf";
  doc.save(fileName);
};

export default DcrSchoolPDF;
